import { useState, useEffect, useRef, useMemo } from 'react';
import { BordCard, type CardLeftContentProps, type CardLeftFeatureTag } from 'bord-design-system';
import SerialInput from 'components/SerialInput/SerialInput';
import { bordColorTheme } from 'styles/styles.constants';

import './MultipleSerialToolCard.scss';

export interface IToolData {
  id?: number;
  productId?: number | null;
  name?: string | null;
  pills?: { [key: string]: string | null };
  image?: string | null;
  brand?: string | null;
  brandImage?: string | null;
}

interface IMultipleSerialToolCardProps {
  tool: IToolData;
  quantity?: number;
  serials?: string[];
  customClassName?: string;
  duplicateSerialIndices?: Set<number>;
  onRemove?: () => void;
  onQuantityChange?: (quantity: number) => void;
  onSerialsChange?: (serials: string[]) => void;
  onSerialErrorChange?: (index: number, hasError: boolean) => void;
  onSerialValidatingChange?: (index: number, isValidating: boolean) => void;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelectionChange?: () => void;
}

/**
 * Tarjeta de herramienta con soporte para múltiples seriales y cantidad editable
 * @param tool - La herramienta a mostrar
 * @param quantity - La cantidad de la herramienta
 * @param serials - Los seriales de la herramienta
 * @param customClassName - Clase personalizada para el componente
 * @param onRemove - Función para remover la herramienta cuando se desmarca el checkbox
 * @param onQuantityChange - Función para manejar el cambio de cantidad
 * @param onSerialsChange - Función para manejar el cambio de seriales
 * @param onSerialErrorChange - Función para manejar el cambio de error de serial
 * @param onSerialValidatingChange - Función para manejar el cambio de validación de serial
 * @param showCheckbox - Define si se muestra el checkbox del BordCard
 * @param isSelected - Estado del checkbox del BordCard
 * @param onSelectionChange - Callback para alternar selección
 */
const MultipleSerialToolCard = ({
  tool,
  quantity = 1,
  serials = [],
  duplicateSerialIndices = new Set(),
  onRemove,
  onQuantityChange,
  onSerialsChange,
  onSerialErrorChange,
  onSerialValidatingChange,
  showCheckbox = true,
  isSelected = true,
  onSelectionChange
}: IMultipleSerialToolCardProps) => {
  const [localQuantity, setLocalQuantity] = useState<number | ''>(quantity);
  const onQuantityChangeRef = useRef(onQuantityChange);
  const onSerialsChangeRef = useRef(onSerialsChange);

  const displayName = tool.name?.trim() ? tool.name : 'Sin nombre';

  const featureTags = useMemo((): CardLeftFeatureTag[] => {
    const tags: CardLeftFeatureTag[] = [];
    if (tool.pills) {
      Object.entries(tool.pills).forEach(([key, value], index) => {
        if (value) {
          tags.push({
            tagKey: `${key}-${index}`,
            labelName: value,
            standardSize: 'small',
          });
        }
      });
    }
    return tags;
  }, [tool.pills]);

  const renderTags = featureTags?.length > 0 ? featureTags?.slice(0, 3) : undefined

  const leftBlock = useMemo((): CardLeftContentProps => {
    return {
      title: displayName,
      titleClassName: 'customLeftBlockTitle',
      featureTags: renderTags,
      bordPhotoProps: {
        url: tool.image || undefined,
        size: 's56',
        variant: 'square',
        toolModelMarkerProps: {
          isBordTool: true,
          customSize: 3.2
        },
        style: {
          border: `1px solid ${bordColorTheme.white[400]}`
        }
      }
    };
  }, [displayName, featureTags, tool.image]);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setLocalQuantity('');
      return;
    }
    const newQuantity = Math.max(1, parseInt(value) || 1);
    setLocalQuantity(newQuantity);
    onQuantityChangeRef.current?.(newQuantity);
  };

  const handleQuantityBlur = () => {
    if (localQuantity === '' || localQuantity < 1) {
      setLocalQuantity(1);
      onQuantityChangeRef.current?.(1);
    }
  };

  const handleBordCardQuantityChange = (newQuantity: number) => {
    const normalizedQuantity = Math.max(1, Number(newQuantity) || 1);
    setLocalQuantity(normalizedQuantity);
    onQuantityChangeRef.current?.(normalizedQuantity);
  };

  const handleCardCheckboxChange = () => {
    if (onSelectionChange) {
      onSelectionChange();
      return;
    }

    if (isSelected && onRemove) {
      onRemove();
    }
  };

  const handleSerialChange = (index: number, serialValue: string) => {
    const newSerials = [...serials];
    newSerials[index] = serialValue;
    onSerialsChangeRef.current?.(newSerials);
  };

  const handleSerialInputChange = (index: number, serialValue: string) => {
    const newSerials = [...serials];
    newSerials[index] = serialValue;
    onSerialsChangeRef.current?.(newSerials);
  };

  const adjustSerialsToQuantity = () => {
    if (localQuantity === '' || serials.length === localQuantity) return;

    const newSerials = [...serials];
    while (newSerials.length < localQuantity) newSerials.push('');
    while (newSerials.length > localQuantity) newSerials.pop();

    onSerialsChangeRef.current?.(newSerials);
  };

  const getDuplicateError = (index: number): string | undefined => {
    return duplicateSerialIndices.has(index) ? 'Verifica que este serial no se repita' : undefined;
  };

  const checkboxConfig =
  showCheckbox
    ? {
      show: true,
      isActive: isSelected,
      onChange: handleCardCheckboxChange
    }
    : undefined

    const quantityRightContent = (
      <div className="quantitySection">
        <label className="quantityLabel">Unidad</label>
        <input
          type="number"
          min={1}
          value={localQuantity}
          onChange={handleQuantityChange}
          onBlur={handleQuantityBlur}
          className="quantityInput"
        />
      </div>
    );
  
    const serialRows = Array.from({ length: typeof localQuantity === 'number' ? localQuantity : 1 }).map((_, index) => (
      <SerialInput
        key={`serial-${tool.id}-${index}`}
        value={serials[index] || ''}
        onValueChange={serialValue => handleSerialChange(index, serialValue)}
        onInputChange={serialValue => handleSerialInputChange(index, serialValue)}
        onErrorChange={hasError => onSerialErrorChange?.(index, hasError)}
        onValidatingChange={isValidating => onSerialValidatingChange?.(index, isValidating)}
        externalError={getDuplicateError(index)}
        label="Serial *"
        customWidth="100%"
      />
    ));

  useEffect(() => {
    onQuantityChangeRef.current = onQuantityChange;
    onSerialsChangeRef.current = onSerialsChange;
  }, [onQuantityChange, onSerialsChange]);

  useEffect(() => {
    adjustSerialsToQuantity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuantity]);

  useEffect(() => {
    setLocalQuantity(prevQuantity => (quantity !== prevQuantity ? quantity : prevQuantity));
  }, [quantity]);

  return (
    <BordCard
      customClassName="multipleSerialToolCard"
      leftBlock={leftBlock}
      checkbox={checkboxConfig}
      rightContent={!showCheckbox ? quantityRightContent : undefined}
      quantityValue={showCheckbox ? (typeof localQuantity === 'number' ? localQuantity : 1) : undefined}
      onQuantityChange={showCheckbox ? handleBordCardQuantityChange : undefined}
      unitLabel="Unidad"
      showUnitControls={showCheckbox}
      bottomContent={<div className="serialsGrid">{serialRows}</div>}
      showBottomContent
    />
  );
};

export default MultipleSerialToolCard;